import { Injectable, OnDestroy } from '@angular/core';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { AgregationService } from '@ispm-client/modules/aggregator/services/aggregator.service';
import { IdataBinding } from '@ispm-client/modules/aggregator/interfaces';
import { get, isEmpty, includes, isEqual,cloneDeep,toPath } from 'lodash';
import { IdynamicForm } from '@ispm/dynamic-form/interfaces/IdynamicForm';
import { Observable, BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { QueryRegistrationTransformationService } from '@ispm-client/modules/pan-cancer/services/query-registration-transformation.service';
import {
  isDerivedClassification,
  isFormItemsClassificationForViewAndShowAll, isFormItemsViewAndDataShowAll, isFormItemsWithDataShowAll
} from '@ispm-client/modules/pan-cancer/utils/count-calc-util';
import { WidgetCountProcessorService } from '@ispm-client/modules/pan-cancer/services/widget-count-processor-service';
import { distinctUntilKeyChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import _ from 'lodash';
import {Store} from "@ngrx/store";
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { BundleCall } from '@ispm-client/modules/aggregator/services';

enum CardType {
  ConfigDriven = 'configDriven',
  DataDriven = 'dataDriven'
}

export const SHOW_ALL_COUNT: string = 'showAllCount';
export const VIEW_COUNT: string = 'viewCount';
export const SHOW_ALL_DATA_COUNT: string = 'showAllDataCount';

export interface  CountInfo {
  viewCount: number;
  showAllCount: number;
  showAllDataCount: number;
  noteCount?: number;
}

export interface  CardCategory {
   type: string;  // configDriven,dataDriven
   classification: {
     viewCount: string, // formItems|data (check for items with countContributor)| derived(check for items with countContributor and emit from widget the count)
     showAllCount: string // formItems |formItemsWithData(check items for showAllCount & data for hidden content) | data(check for items with countContributor) | derived(call the widget with the item of showAll to provide the groupped count)
   };
}

export interface CountProcessor {
  formData: any;
  countType: string;
  isTotalItemCount?: boolean;
  dynamicFormConfig: IdynamicForm;
  viewConfiguredCount?: string;
}


@Injectable()
export class CardStatisticsService implements OnDestroy {


  private responseStatusCompleted: boolean[];

  constructor(private _formHandler: FormHandlerService,
              private aggregatorService: AgregationService,
              private _queryRegService: QueryRegistrationTransformationService,
              private genomicsStore: Store<IgenomicsState>,
              private _bundleCall: BundleCall,
              private  widgetCountProcessorService: WidgetCountProcessorService) {

  }

  private _countInfo: CountInfo = { viewCount: 0, showAllCount: 0 , showAllDataCount: 0, noteCount:0};

  private cardCategory: { type: string };

  private countEmitter: Subject<CountInfo> = new BehaviorSubject<CountInfo>(null);

  private externalCountInformation: Subject<any> = new BehaviorSubject<any>(null);

  requestDataForForms(formHolder: any): Observable<CountInfo> {
    if ((formHolder['cardCategory'] &&  formHolder.cardCategory['type'] === 'configDrivenDataCount') && (formHolder['showAllForms'].length > 0) ) {
      this.fetchFormDataBindings(formHolder.showAllForms[0]['formId'], 'showAllCount')
        .subscribe((responseItem) => {
          this.emitCountInfoWhenConfigDrivenData(responseItem);
        });
    }
    //This Card category is for the hybrid Card's which contain both data driven and config drive data in a card
    else if ((formHolder['cardCategory'] && formHolder.cardCategory['type'] === 'hybridCardDataCount') ) {
      const showAllFormObservables: Observable<any>[] = formHolder.showAllForms.map((form) => {
        return this.fetchFormDataBindings(form['formId'], 'showAllCount').pipe(
          map((response) => {
            const countProcessor: CountProcessor = {
              formData: response.formData,
              countType: response.countType,
              dynamicFormConfig: this.filterformHiddenItems(response.formConfig),
            };
            return this.groupFormDataId(countProcessor);
          })
        );
      });
    
      const viewFormObservables: Observable<any>[] = formHolder.viewForms.map((form) => {
        return this.fetchFormDataBindings(form['formId'], 'viewCount').pipe(
          map((response) => {
            const countProcessor: CountProcessor = {
              formData: response.formData,
              countType: response.countType,
              dynamicFormConfig: this.filterformHiddenItems(response.formConfig),
            };
            return this.groupFormDataId(countProcessor);
          })
        );
      });
    
      combineLatest([...showAllFormObservables, ...viewFormObservables]).subscribe((data) => {

        /* store the id's from show All data Payload in showAllFormDataIdList*/
        let showAllFormDataIdList : any[] = [];
         data.slice(0, formHolder.showAllForms.length).forEach(item => {
          if(Array.isArray(item)){
            item.forEach(data => {
              showAllFormDataIdList.push(data);
            })
          }
          else{
            showAllFormDataIdList.push(item);
          }
        });
        /* store the id's from view data Payload in viewFormDataIdList*/
        let viewFormDataIdList: any[] = []; 
        data.slice( formHolder.showAllForms.length).forEach(item => {
          if(Array.isArray(item)){
            item.forEach(data => {
              viewFormDataIdList.push(data);
            })
          }
          else{
            viewFormDataIdList.push(item);
          }
        });
        let showAllDataCount = 0;
        /* Increase hidden item count if an id is present in  showAllFormDataIdList but not in viewFormDataIdList
        to calculate total hidden item count*/
        showAllFormDataIdList.forEach((showAllCounts) => {
            if ( !viewFormDataIdList.includes(showAllCounts)) {
              showAllDataCount += 1;
            }
        });
        const countInfo: CountInfo = {
          showAllCount: 0,
          viewCount: 0,
          showAllDataCount: showAllDataCount,
        };
    
        this.countEmitter.next(countInfo);
      });
    }
    else if ( formHolder['cardCategory'] &&  formHolder.cardCategory['type'] === 'dataDriven') {
      this.constructFormRequest(formHolder);
    } else if ( formHolder['cardCategory'] && formHolder.cardCategory['type'] === 'configDriven') {
      let viewForm: any[] = [];
      if ( formHolder.viewForms.length > 0) {
        this._formHandler.getForm(formHolder.viewForms[0].formId)
          .subscribe(form => {
            viewForm = this.filterformHiddenItems(form);
         if (formHolder.viewForms[0]['showCount']) {
             this._countInfo['viewCount'] = parseInt(formHolder.viewForms[0]['showCount'], 10); // FIXME Check if the we need some other change here
             this._countInfo['showAllCount'] = viewForm.length;
           }
          this.countEmitter.next(this._countInfo);
        });
      }
     const showAllClassification = get(formHolder, 'cardCategory.classification.showAllCount', null);
      if (formHolder['showAllForms'] && formHolder['showAllForms'].length > 0 && (showAllClassification === 'data' || showAllClassification === 'formItemsWithData')) {

      combineLatest( [this.fetchFormDataBindings(formHolder.showAllForms[0]['formId'], 'showAllCount'),
      this.fetchFormDataBindings(formHolder.showAllForms[1]['formId'], 'showAllCount')]
      ).pipe(
        map(([resp1,resp2]) => ({ resp1 ,resp2})),
      )
         .subscribe((response) => {
          let isNoteViewinDashboard; //this variable is used to check whether view is avaible in
          //the dashboard if avaible then don't add the count
          if (formHolder.viewForms && formHolder.viewForms[1] && formHolder.viewForms[1]['isNoteViewAvailable']) {
            isNoteViewinDashboard = formHolder.viewForms[1]['isNoteViewAvailable'];
          }


           const countProcessor: CountProcessor = {
             formData: response.resp1.formData,
             countType: response.resp1.countType,
             dynamicFormConfig: this.filterformHiddenItems(response.resp1.formConfig)

           };
           const countProcessor2: CountProcessor = {
            formData: response.resp2.formData,
            countType: response.resp2.countType,
            dynamicFormConfig: this.filterformHiddenItems(response.resp2.formConfig)

          };

           countProcessor['isTotalItemCount'] = true; // this means for item in form what is the data recieved is considered for the count
          const countinfo1 =  cloneDeep(this.processCountAnalysis(countProcessor, formHolder['cardCategory']));
          const countinfo2 = cloneDeep(this.processCountAnalysis(countProcessor2, formHolder['cardCategory']));
          this.getNoteViewCount(isNoteViewinDashboard,countinfo1,countinfo2)

          /* if in layout configuration if showCount is in nested structure and then
             we need to add showCount in layout in both showAllForms and
            viewAllForms then addCount variable will take care of the hiddenItemCount for example
            risk factor card */
              if(formHolder &&formHolder.viewForms[0] && formHolder.viewForms[0]['addCount']) {
                let showCount = formHolder.showAllForms[0]?.showCount ? formHolder.showAllForms[0]?.showCount : 0;
                if(isNoteViewinDashboard) {
                  this._countInfo.showAllCount = countinfo1.showAllCount  + parseFloat(showCount)
                } else {
                  this._countInfo.showAllCount = countinfo1.showAllCount + countinfo2.showAllCount  + parseFloat(showCount)
                }
              }

          this.triggerShowAllDataCount(formHolder);

           this.countEmitter.next(this._countInfo);
         });
      }
      else if(formHolder['showAllForms'] && formHolder['showAllForms'].length > 0 && showAllClassification === 'formItems'){
        combineLatest(
          [this.fetchFormDataBindings(formHolder.showAllForms[0]['formId'], 'showAllCount'),
          this.fetchFormDataBindings(formHolder.showAllForms[1]?.formId, 'showAllCount')]
          )
          .pipe(
            map(([resp1,resp2]) => ({ resp1,resp2 })),
          )
          .subscribe((response) => {
            const countProcessor: CountProcessor = {
              formData: response.resp1.formData,  // since only interested in the formItems
              countType: response.resp1.countType,
              dynamicFormConfig: this.filterformHiddenItems(response.resp1.formConfig)
            };
            const countProcessor1: CountProcessor = {
              formData: response.resp2.formData,  // since only interested in the formItems
              countType: response.resp2.countType,
              dynamicFormConfig: this.filterformHiddenItems(response.resp2.formConfig)
            };
            let isNoteViewinDashboard; //this variable is used to check whether view is available in
            //the dashboard if avaible then don't add the count
            if (formHolder.viewForms && formHolder.viewForms[1] && formHolder.viewForms[1]['isNoteViewAvailable']) {
              isNoteViewinDashboard = formHolder.viewForms[1]['isNoteViewAvailable'];
            }


            let countProcessInfoOne =  cloneDeep(this.processCountAnalysis(countProcessor, formHolder['cardCategory']));
            const countProcessInfoTwo =  cloneDeep(this.processCountAnalysis(countProcessor1, formHolder['cardCategory']));
            /* Fixed hidden tnmCount itemcount */
            if (formHolder.viewForms && formHolder.viewForms[0] && formHolder.viewForms[0]['tnmCount']) {
              this.getComponentFormDataShowAllCount().subscribe((context) => {
                    const formData = countProcessor.formData[0]?.tumor_info_edit;
                    const showAlllData = (!isEmpty(formData)) ? formData.filter((data) => isEqual(data[`${countProcessor.dynamicFormConfig.inputs[0].widgetPayload.context}`], context)) : [];
                    countProcessInfoOne.showAllCount = showAlllData.length;
                    this.getNoteViewCount(isNoteViewinDashboard,countProcessInfoOne,countProcessInfoTwo,formHolder?.viewForms[0]?.addAllField,formHolder.viewForms[0]['tnmCount'])
                    this.countEmitter.next(this._countInfo);
                    this.triggerShowAllDataCount(formHolder);
                  });
            }
            else {
              this.getNoteViewCount(isNoteViewinDashboard,countProcessInfoOne,countProcessInfoTwo,formHolder?.viewForms[0]?.addAllField,formHolder.viewForms[0]['tnmCount'])
              this.countEmitter.next(this._countInfo);
            }
          });
      }
      this.triggerShowAllDataCount(formHolder);
    }

    return this.countEmitter.asObservable();
  }

  /**
   * Returns the show all count for the component form data
   */
  private getComponentFormDataShowAllCount(): Observable<any> {
    return this.genomicsStore.select('tests').pipe(
      map(tests => tests?.diagnosticOrders?.order), // Extract diagnosticOrders.order
      distinctUntilKeyChanged('id'), // Compare based on the 'id' key
      switchMap(order => {
        if (!order?.condition) {
          return of(null);
        }

        const reference = order.condition.reference;
        return this._bundleCall.searchCall(this.getCurrentCancerType(reference)).pipe(
          map(data => data[0]?.response?.entry[0]?.resource?.cancerType),
          shareReplay(1) // Cache and replay the response for subsequent subscribers
        );
      })
    );
  }

  /**
   * Fetches current cancer type
   */
  private getCurrentCancerType(reference) {
    return {
      'queries': [{
        'targetResource': 'Condition',
        'queryString': `_id=${reference}&subResourceTypeCode=primary_cancer&categoryCode=primary&_fieldslist=cancerType&_count=1`,
      }]
    }
  }

  /**
   * Emits count info when classification is ConfigDrivenData
   * used in forms where hidden count is contributed to view when data
   * is present in form items
   * @param {formItemsData} holds the form data
   */
  private emitCountInfoWhenConfigDrivenData(formItemsData) {
    /**
     * since we are only intrested only in data present in form items, we are emiting
     * the count contributed as showAllDataCount
     * The note count and others are kept 0 for now can be extended to do any calculation when Notes is present and neede any calculation
     */

    const showAllDataCount: number = this.processshowAllDataCount(formItemsData.formData);
    const countInfo: CountInfo = {
      showAllCount: 0,
      showAllDataCount: showAllDataCount,
      viewCount: 0,
      noteCount: 0
    };
    this.countEmitter.next(countInfo);
  }

  /**
   * Processes show all data count and returns
   * @param {formData} holds the form data
   * @returns showAll data count number
   */
  private processshowAllDataCount(formData: any[]): number {
    const showAllDataCountLength: any[] = formData.filter((data: any) => {
      return  _.values(data).every((x) => !isEmpty(x));
    });
    return showAllDataCountLength.length;
  }
  subscribeToExternalCountEmission() {
       return this.externalCountInformation.asObservable();
  }


  /* This function check if the notes is available in the dashboard if present return total item
  else return sum of total count*/

  private getNoteViewCount(isNoteViewinDashboard,countProcessInfoOne,countProcessInfoTwo,addAllField?:boolean,tnmCount?:boolean) {

    if(isNoteViewinDashboard) {
      this._countInfo.showAllCount = countProcessInfoOne.showAllCount;
    } else {
      this._countInfo.showAllCount = countProcessInfoOne.showAllCount + countProcessInfoTwo.showAllCount;
    }

  /*if data is available but it is less then the view count and notes data is also
  available then in that case we need to show hidden item count for notes
  if data is available but it is less then the view count and notes data is also
     not available then in that case we don't need to show the hidden item count */

  /*if countInfoOne is not having data and only notes is having data we need to show count
   to handle tnmCount */
   let noteCountCondition = (countProcessInfoTwo.showAllCount && addAllField !== true && (this._countInfo['viewCount'] > this._countInfo['showAllCount']) && !isNoteViewinDashboard);
    if(noteCountCondition ) {
      this._countInfo.noteCount = countProcessInfoTwo.showAllCount;
    } else if (!noteCountCondition && countProcessInfoTwo.showAllCount > 0 && countProcessInfoOne.showAllCount <=0 && tnmCount) {
      this._countInfo.noteCount = countProcessInfoTwo.showAllCount;
    } else {
      this._countInfo.noteCount = 0;

    }

  }
 /**
   * Core processor for all cards  count derivation.
   * @param {CountProcessor} countProcessor
   */

private  groupFormDataId(countProcessor: CountProcessor): any {
    let totalItemIdList: string[] = [];
    const formConfiguration = countProcessor['dynamicFormConfig'];
    let formData   = countProcessor['formData'];
    formConfiguration.inputs.forEach(formItem => {

    /* Iterate through hierarchal json data getting from the UI in the payload and get the unique id's of 
    each item in the json payload and return the list of item id's in the payload*/
    
   
    const itemList = this.processFormItemAndGetIDs((formData), formItem['name'], formItem);
    itemList.forEach(item => {
      totalItemIdList.push(item);
    });
  });
    return totalItemIdList;
  }

  private processFormItemAndGetIDs(obj: any, targetKey: string, formItem: any): any{
    let eachItemIdList : string[] = [];
    function getFormItemId(item: any) {
      if (item && typeof item === "object") {
        if (item[targetKey]) {
          if (Array.isArray(item[targetKey])) {
            for (const nestedItem of item[targetKey]) {
              if (nestedItem && nestedItem.id && !eachItemIdList.includes(nestedItem.id)) {
                eachItemIdList.push(nestedItem.id);
              }
              getFormItemId(nestedItem);
            }
          } else if (typeof item[targetKey] === "object") {
            const nestedItem = item[targetKey];
            if (nestedItem && nestedItem.id && !eachItemIdList.includes(nestedItem.id)) {
              eachItemIdList.push(nestedItem.id);
            }
            getFormItemId(nestedItem);
          }
          else if(typeof item[targetKey] === "string" ){
             if ( !eachItemIdList.includes(item['id'])) {
              eachItemIdList.push(item['id']);
            }
          }
        } else {
          for (const key in item) {
            const value = item[key];
            getFormItemId(value);
          }
        }
      }
    }
    getFormItemId(obj);
    if(formItem.type.toUpperCase() === 'WIDGET'&& (formItem.options.component === 'GroupTableComponent' || formItem.options.component === 'ChartWidgetComponent' || formItem.options.component === 'DynamicContextFormComponent'
    || formItem.options.component === 'SegmentFindingsTableComponent')){
      const viewAll = formItem.widgetPayload.viewAll;
      if(!viewAll){
        if(formItem.widgetPayload.showCount)
        eachItemIdList = eachItemIdList.slice(0, formItem.widgetPayload.showCount);
      }
      
    }
    return eachItemIdList;
  }

  filterformHiddenItems(form){
    const formConfiguration = form;
    formConfiguration.inputs = form.inputs.filter(input =>
      !includes(input['cssClass'], 'hidden-form-item'))
   return  formConfiguration;
  }

  getObservableForCountEmission() {
    return this.externalCountInformation;
  }

  filterCountContributor(dataBindings: IdataBinding[]) {
   return !isEmpty(dataBindings) ? dataBindings.filter(dataBinding => !!dataBinding.countContributor) : [];
  }

  isDepandantDataBinding(dataBindings: IdataBinding[]) {
    return !isEmpty(dataBindings) ? dataBindings.filter(dataBinding => !!dataBinding.isDepandant) : [];
  }

  fetchFormDataBindings(formId, countType: string): Observable<any> {
    let formConfiguration: any;
   return this._formHandler.getForm(formId)
      .switchMap( form => {
        const dataBindings =[];
      if(Array.isArray(form.dataBinding) && form.dataBinding.length>0){
        form.dataBinding.forEach(dataBinding=>{
          dataBindings.push({...dataBinding});
        });
        delete form.dataBinding;
        form.dataBinding=dataBindings
      };
      formConfiguration={...form};
        //this will avoid mutation
        if (countType === 'showAllDataCount') {
          if (this.isDepandantDataBinding(formConfiguration['dataBinding']).length > 0) {
            return this._queryRegService.queryRegistration(formConfiguration['dataBinding']);
          } else {
            return this.aggregatorService.registerQuery(formConfiguration['dataBinding']);
          }
        } else {
          return this.aggregatorService.registerQuery(this.filterCountContributor(formConfiguration['dataBinding']));
        }
      })
     .switchMap(dataValue => {
          return Observable.of({ formConfig: formConfiguration, formData: dataValue, countType: countType});
        });
  }

   constructFormRequest(formHolder: any) {

        if (formHolder.viewForms.length > 0 ) {
          this.fetchFormDataBindings(formHolder.viewForms[0]['formId'], 'viewCount')
            .subscribe((responseItem) => {
                const countProcessor: CountProcessor = {
                formData: responseItem.formData,
                countType: responseItem.countType,
                dynamicFormConfig: responseItem.formConfig

              };
                this.processCountAnalysis(countProcessor, formHolder['cardCategory']);
                this.triggerShowAllDataCount(formHolder);
                this.countEmitter.next(this._countInfo);

          });
        }
        if (formHolder.showAllForms.length > 0 ) {
          combineLatest( [this.fetchFormDataBindings(formHolder.showAllForms[0]['formId'], 'showAllCount'),
          this.fetchFormDataBindings(formHolder.showAllForms[1]['formId'], 'showAllCount')]

          )
          .
          pipe(
            map(([resp1, resp2]) => ({ resp1, resp2 })),
          )
            .subscribe((responseItem) => {
              const countProcessor1: CountProcessor = {
                formData: responseItem.resp1.formData,
                countType: responseItem.resp1.countType,
                dynamicFormConfig: responseItem.resp1.formConfig
              };
              const countProcessor2: CountProcessor = {
                formData: responseItem.resp2.formData,
                countType: responseItem.resp2.countType,
                dynamicFormConfig: responseItem.resp2.formConfig
              };

              const countProcess1= cloneDeep(this.processCountAnalysis(countProcessor1, formHolder['cardCategory']));
              const countProcess2 = cloneDeep(this.processCountAnalysis(countProcessor2, formHolder['cardCategory']));
              if((formHolder &&formHolder.viewForms[0] && formHolder.viewForms[0]['addView'])) {

                  countProcess1.showAllCount = countProcess1.showAllCount - formHolder.showAllForms[0].showCount;
              }
              this._countInfo.showAllCount = countProcess1.showAllCount + countProcess2.showAllCount;
                 if(countProcess2.showAllCount && this._countInfo['viewCount'] >= this._countInfo['showAllCount']
                 && (formHolder &&formHolder.viewForms[0] && formHolder.viewForms[0]['addView'])) {
                this._countInfo.noteCount = countProcess2.showAllCount;
                  } else {
                    this._countInfo.noteCount = 0;
                  }

                 /*to handle radio and pathology as image is present so we only intrested in
                    notes data count*/
                  if(formHolder &&formHolder.viewForms[0] && formHolder.viewForms[0]['addView'] === false) {
                    this._countInfo.showAllCount =countProcess2.showAllCount;
                  }

              this.triggerShowAllDataCount(formHolder);
              this.countEmitter.next(this._countInfo);
          });
        }
  }

  determineShowAllDataCount(formId: string, cardCategory: CardCategory, viewConfiguredCount?: string,formHolder?:any) {

    if (cardCategory['type'] === 'configDriven') {
      this.fetchFormDataBindings(formId, 'showAllDataCount')
       // .pipe(takeUntil(this.countEmitter))  //TODO Fix the subscriptions to unsubscribe takeUtil not working
        .subscribe((responseItem) => {
          let  formConfig = responseItem.formConfig;
          formConfig =  this.filterformHiddenItems(responseItem.formConfig)
          const countProcessor: CountProcessor = {
            formData: cloneDeep(responseItem.formData),
            countType: responseItem.countType,
            dynamicFormConfig: cloneDeep(formConfig)
          };
          countProcessor['viewConfiguredCount'] = viewConfiguredCount;
          this._countInfo = this.processCountAnalysis(countProcessor, cardCategory );

        /* to handle radio and pathology as image is present so we only intrested in
        notes data count we don't need to minus it and some case when image is not present then
         we need to minus it to get actual hidden itemcount (pathalogy and radiology
         endoscopy ,medical history , pathology, radiology , physical status)*/
         if( (formHolder.viewForms && formHolder.viewForms[0] && formHolder.viewForms[0]['addView']) || formHolder?.viewForms[0]?.endoscopyViewCard) {
          this._countInfo['showAllDataCount'] = this._countInfo['showAllCount']

          }else {
              this._countInfo['showAllDataCount'] = this._countInfo['showAllCount'] - this._countInfo['viewCount'];

          }

         this.countEmitter.next(this._countInfo);
      });
    } else if (cardCategory['type'] === 'dataDriven') {

      if (this._countInfo.hasOwnProperty('viewCount') && this._countInfo.hasOwnProperty('showAllCount') && this._countInfo['viewCount'] <= this._countInfo['showAllCount']
      ) {
            this._countInfo['showAllDataCount'] = this._countInfo['showAllCount'] - this._countInfo['viewCount'];
        this.countEmitter.next(this._countInfo);
      }
    }

  }

  /**
   * Core processor for all cards  count derivation.
   * @param {CountProcessor} countProcessor
   * @param {CardCategory} cardCategory
   */
   processCountAnalysis(countProcessor: CountProcessor, cardCategory: CardCategory) {
     let formData = countProcessor['formData'];
     const countType = countProcessor['countType'];
     if (Array.isArray(formData)) {
     formData = formData.reduce(function(result, item) {
      Object.keys(item).forEach(itemKey => {
        result[itemKey] = item[itemKey];
      });
      return result;
    }, { });
  }
    if (countProcessor['dynamicFormConfig']) {
      const formConfiguration = countProcessor['dynamicFormConfig'];
      this._countInfo[countType] = 0;
      const viewCountClassification = get(cardCategory, 'classification.viewCount', null);
      const showAllViewClassification = get(cardCategory, 'classification.showAllCount', null);
      const isFormItemsBoth = isFormItemsClassificationForViewAndShowAll(viewCountClassification, showAllViewClassification);
      const isFormItemsAndData = isFormItemsViewAndDataShowAll(viewCountClassification,showAllViewClassification);
      const isFormItemsWithData = isFormItemsWithDataShowAll(showAllViewClassification); //TODO re-think if this logic is required.
      if (isFormItemsWithData && countType === 'showAllCount') {
        return;
      }
      /**
       * Incase both are formItems , we use the viewConfiguredCount to remove items from showAll,
       * as those will be already shown in the dashboardView.
       */
      if (countProcessor['viewConfiguredCount'] && isFormItemsBoth) {
        formConfiguration.inputs.splice(0, parseInt(countProcessor['viewConfiguredCount'], 10));
      }
      /**
       * InCase both are formItems, for showAllCount we need to only find the length of input items .
       */
      else if (isFormItemsBoth) {
        if(formData && formData.notes) {
          this._countInfo[countType]++;

        } else if(formData) {
          formConfiguration.inputs.forEach(formItem => {
            const newdata = this.getItemValue(formConfiguration ,formItem,formData,cardCategory);
            if(newdata.itemValue && !isEmpty(newdata.itemValue)) {
              this._countInfo[countType]++;
            }

           });

        }


        return this._countInfo;
      }

      formConfiguration.inputs.forEach(formItem => {
        /**
         * This is to convert if formData is Array, each object in the array is split with keys.
         * The keys will be part of new Object with associated value .
         */
        if (Array.isArray(formData)) {
          formData = formData.reduce(function(result, item) {
            Object.keys(item).forEach(itemKey => {
              result[itemKey] = item[itemKey];
            });
            return result;
          }, { });
        }



        /**
         * Pick the dataBinding based on the formItem Name
         */

        let formItemResponse = this.getItemValue(formConfiguration ,formItem,formData,cardCategory);
          /**
         * When the view form is form items and showAll is based on the data of each item
         * to identify if showAll exceeds with respect to information shown in the view is determined
         * based on the view reference count ,which specifices count of information that is shown
         * in the view for the related item in showAll.
         */
        if (isFormItemsAndData && countType === 'showAllDataCount' && formItemResponse.dataBinding['viewReferenceCount'] && Array.isArray(formItemResponse.itemValue)) {
          const viewReferenceCount = parseInt(formItemResponse.dataBinding['viewReferenceCount'],10);
          if (formItemResponse.itemValue.length <= viewReferenceCount) { // Added this as splice doesn't work if lenght less that deleteCount
            formItemResponse.itemValue = [];
          } else {
            formItemResponse.itemValue.splice(0,viewReferenceCount);
            if (countType === 'showAllDataCount' && !isEmpty(formItemResponse.itemValue)) {
              //this code will help with Exact Count of Hidden Items for RiskFactor Card we are Assigning ItemValue-1 as by default Line no 343 will increase by 1
              this._countInfo[countType] += formItemResponse.itemValue.length - 1;

            }
            }

        }
        const classification = get(cardCategory, 'classification', null);
        // Process if the item is a widget
        /**
         * Incase classification is derived , the items of the form are passed to widgetProcessor.
         */
        if ( formItem.type?.toUpperCase() === 'WIDGET' && isDerivedClassification(classification, countType )) {
          formItemResponse.itemValue = this.widgetCountProcessorService.processWidgetToDetermineContentShown(formItem, formItemResponse.itemValue);

        }

        if(formItem.name === 'notes'&& (formData.notes) ) {
          this._countInfo[countType]++;
        }




        else if (countProcessor['isTotalItemCount'] && !isEmpty(formItemResponse.itemValue) && Array.isArray(formItemResponse.itemValue)) {
          this._countInfo[countType] = this._countInfo[countType] + formItemResponse.itemValue.length;

        }
        else if (countProcessor['isTotalItemCount'] && !isEmpty(formItemResponse.itemValue)) { // This is handled for showall of config driven

          this._countInfo[countType]++;

        }
        else if (!isEmpty(formItemResponse.itemValue) && Array.isArray(formItemResponse.itemValue)) {

            this._countInfo[countType] = formItemResponse.itemValue.length;

        }
      });


    }
  // Currently not in use , as we are not using external subscription for count from the form.
    if (countProcessor['isExternal']) {
      if (!isEmpty(formData) && Array.isArray(formData)) {
        this._countInfo[countType] = formData.length;
      }
    }
    return  this._countInfo;
  }
  /** calculate itemValue  Pick the dataBinding based on the formItem Name*/


  getItemValue(formConfiguration, formItem, formData, cardCategory) {
    const dataBindingIndex = formConfiguration['dataBinding'].findIndex((dataBinding) => {
      /**
       * When formItem name is having path , then the first item in the path would be the dataBinding
       *  identifier
       */
      if (toPath(formItem['name']).length > 1) {
        return dataBinding['identifier'] === toPath(formItem['name'])[0];
      }
      return dataBinding['identifier'] === formItem['name'];
    }
    );
    let dataBinding;
    let itemValue;
    if (dataBindingIndex > -1) {
      dataBinding = formConfiguration['dataBinding'][dataBindingIndex];
      /**
       * Ignore the item Value if it was a default Value from formItem or DataBinding
       */
      if (dataBinding && isEqual(dataBinding.defaultValue, get(formData, formItem['name'])) || isEqual(formItem.defaultValue, get(formData, formItem['name']))) {
        itemValue = undefined;
      }
      else {
        // adding cardCategory.type!=='dataDriven' as for dataDriven card we don't consider path to Fields.
        if (formItem.pathToField && cardCategory.type !== 'dataDriven' && !Array.isArray(get(formData, formItem['name']))) {
          const path = `${formItem['name']}.${formItem.pathToField}`;
          itemValue = get(formData, `${path}`);
        } else {
          itemValue = get(formData, formItem['name']);
        }

      }

    }
    return { dataBinding: dataBinding, itemValue: itemValue }
  }


  /**
   * This triggers the count calculation for the hidden data that is not shown in view .
   * We use the showAll form for the showAllDataCount .
   * @param formHolder
   */
  triggerShowAllDataCount(formHolder: any) {
    let viewCountConfigured;
    if (formHolder.viewForms && formHolder.viewForms[0] && formHolder.viewForms[0]['showCount']) {
      viewCountConfigured = formHolder.viewForms[0]['showCount'];
    }
    // if( formHolder.viewForms && formHolder.viewForms[0] && formHolder.viewForms[0]['addView']) { // to handle pathology radiology card
    //   this._countInfo['viewCount'] = viewCountConfigured;

    // }
    /**
     * Incase ShowAll form is different we check for the form else we use the view form for showAllDataCount.
     */
    if ( formHolder['showAllForms'] && formHolder.showAllForms.length > 0 && formHolder['cardCategory']) {
      this.determineShowAllDataCount(formHolder.showAllForms[0].formId, formHolder['cardCategory'], viewCountConfigured,formHolder);
    } else {
      this.determineShowAllDataCount(formHolder.viewForms[0].formId, formHolder['cardCategory'], viewCountConfigured,formHolder);
    }
  }



  ngOnDestroy () {
    this.countEmitter.complete();
  }

}
